import { attachmentResourceType } from "@/modules/attachment/attachmentResourceConsts";
import { AttachmentWithUrlResource } from "@/modules/attachment/attachmentResourceStruct";
import { HandbookResource } from "@/modules/handbooks/api/resources/handbook/handbookResourceStruct";
import { GetTutorialProgressResponseStruct } from "@/modules/tutorials/api/queries/tutorialProgress/getTutorialProgressQuery";
import {
  handbookResourceType,
  tutorialStepResourceType,
} from "@/modules/tutorials/api/resources/tutorialStep/tutorialStepResourceConsts";
import { TutorialStepResource } from "@/modules/tutorials/api/resources/tutorialStep/tutorialStepResourceStruct";
import { takenTestQuestionSingleResourceType } from "@/modules/tutorialTest/resources/takenTestQuestionSingle/takenTestQuestionSingleResourceConsts";
import { TakenTestQuestionSingleResourceType } from "@/modules/tutorialTest/resources/takenTestQuestionSingle/takenTestQuestionSingleResourceStruct";

export type TestQuestionType = {
  id: string;
  content: string;
  answers: string[];
};

export function parseTutorialProgress(
  response: GetTutorialProgressResponseStruct
) {
  const {
    data: { id, attributes, meta, relationships },
    included,
  } = response;

  const steps = included.filter(
    (item): item is TutorialStepResource =>
      item.type === tutorialStepResourceType
  );
  const attachments = included.filter(
    (item): item is AttachmentWithUrlResource =>
      item.type === attachmentResourceType
  );

  const handbooks = included.filter(
    (item): item is HandbookResource => item.type === handbookResourceType
  );
  const questions = included
    .filter(
      (item): item is TakenTestQuestionSingleResourceType =>
        item.type === takenTestQuestionSingleResourceType
    )
    .map(({ id, attributes }) => ({
      id,
      content: attributes.content,
      answers: attributes.answers,
    }));

  const mappedSteps = steps.map(({ id, attributes, relationships }) => {
    const stepAttachmentsIds = relationships.attachments.data.map(
      ({ id }) => id
    );

    const stepAttachments = attachments
      .filter((attachment) => stepAttachmentsIds.includes(attachment.id))
      .map(({ id, attributes }) => ({
        id,
        fileName: attributes.fileName,
        contentType: attributes.contentType,
        url: attributes.url,
      }));

    const currentHandbook = handbooks.find(
      (handbook) => handbook.id === relationships.handbook.data?.id
    );

    const handbookFile = attachments.find(
      (attachment) =>
        attachment.id === currentHandbook?.relationships.attachment.data.id
    );

    return {
      id,
      name: attributes.name,
      description: attributes.description,
      videoLink: attributes.videoLink,
      stepAttachments,
      handbook:
        currentHandbook && handbookFile
          ? {
              name: currentHandbook.attributes.name,
              id: handbookFile.id,
              contentType: handbookFile.attributes.contentType,
              fileName: handbookFile.attributes.fileName,
              url: handbookFile.attributes.url,
            }
          : null,
    };
  });

  const testQuestions = relationships.testQuestions.data
    .map((questionIdentifier) =>
      questions.find((question) => question.id === questionIdentifier.id)
    )
    .filter((question): question is TestQuestionType => Boolean(question));

  return {
    id,
    name: attributes.name,
    pointsNumber: attributes.pointsNumber,
    tutorialStepsCompleted: meta.tutorialStepsCompleted,
    testPassed: meta.testPassed,
    completedAt: meta.completedAt,
    updatedAt: attributes.updatedAt,
    createdAt: attributes.createdAt,
    steps: relationships.tutorialSteps.data.map((step) => {
      const currentStep = mappedSteps.find(
        (mappedStep) => mappedStep.id === step.id
      );
      return currentStep;
    }),
    testQuestions,
  };
}
